import AppBar from '@mui/material/AppBar'
import Container from '@mui/material/Container'
import { alpha } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import Layout from 'components/Layout'
import { Link2Show } from 'lib/links'
import { getSeriesAtoZ } from 'lib/series'
import { caching, testSupportsSmoothScroll } from 'lib/utils'
import Head from 'next/head'
import React, { useRef } from 'react'

const useStyles = makeStyles((theme) => ({
  appbar: {
    top: '48px',
    [theme.breakpoints.up('sm')]: {
      top: '64px'
    },
    backgroundColor: alpha(theme.palette.background.default, 0.6)
  },
  content: {
    marginTop: '48px',
    [theme.breakpoints.up('sm')]: {
      marginTop: '64px'
    }
  },
  title: {
    marginBottom: theme.spacing(3)
  },
  tab: {
    minWidth: '30px'
  },
  indicator: {
    backgroundColor: theme.palette.primary.main
  },
  list: {
    columnCount: 1,
    [theme.breakpoints.up('sm')]: {
      columnCount: 2
    },
    [theme.breakpoints.up('md')]: {
      columnCount: 3
    },
    listStyleType: 'none',
    paddingLeft: 0
  },
  link: {
    color: theme.palette.text.primary,
    display: 'block',
    width: '100%',
    paddingTop: '6px',
    paddingBottom: '6px',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    },
    fontSize: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}))

export default function AtoZ ({ sections, series }) {
  const classes = useStyles()
  const [value, setValue] = React.useState('0-9')
  const refs = {}
  const supportsSmoothScroll = testSupportsSmoothScroll()

  const handleChange = (event, newValue) => {
    setValue(newValue)
    const ref = refs[newValue]
    if (supportsSmoothScroll) {
      window.scrollTo({ top: ref.current.offsetTop - 120, behavior: 'smooth' })
    } else {
      window.scrollTo(0, ref.current.offsetTop - 120)
    }
  }

  return (
    <>
      <Head>
        <title>Sendung verpasst? Alle Sendungen von A-Z</title>
        <meta name='description' content='Übersicht über alle Sendungen zum Stöbern' />
      </Head>
      <Layout>
        <AppBar color='transparent' className={classes.appbar} position='fixed'>
          <Container maxWidth='lg'>
            <Tabs
              variant='scrollable'
              value={value}
              onChange={handleChange}
              scrollButtons
              classes={{ indicator: classes.indicator }}
              allowScrollButtonsMobile
              textColor='#fff'
            >
              {sections.map((section, index) => (
                <Tab key={index} label={section} value={section} className={classes.tab} />
              ))}
            </Tabs>
          </Container>
        </AppBar>
        <div className={classes.content}>
          <Typography component='h1' variant='h1' className={classes.title}>Alle Sendungen</Typography>
          {series.map((item, index) => {
            const ref = useRef(null)
            refs[item.letter] = ref
            return (
              <section id={'section-' + item.letter} key={index} ref={ref}>
                <Typography component='h2' variant='h2'>{item.letter}</Typography>
                <ul className={classes.list}>
                  {item.items.map((series, index2) => (
                    <li key={index2}>
                      <Link2Show slug={series.slug}>
                        <a
                          className={classes.link}
                          data-test='show-link'
                          dangerouslySetInnerHTML={{ __html: series.name }}
                        />
                      </Link2Show>
                    </li>
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      </Layout>
    </>
  )
}

export async function getServerSideProps (context) {
  caching(context.res, process.env.CACHE_SHOW)

  const series = await getSeriesAtoZ()
  const sections = series.map(y => y.letter)

  return {
    props: {
      sections,
      series
    }
  }
}
